import React, { createContext, useContext, useEffect, useState } from 'react';

type Theme = 'light' | 'dark';

interface ThemeContextType {
	theme: Theme;
	setTheme: (theme: Theme) => void;
	toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
	const context = useContext(ThemeContext);
	if (context === undefined) {
		throw new Error('useTheme must be used within a ThemeProvider');
	}
	return context;
};

interface ThemeProviderProps {
	children: React.ReactNode;
	defaultTheme?: Theme;
	storageKey?: string;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({
	children,
	defaultTheme = 'light',
	storageKey = 'payment-statement-theme',
}) => {
	const [theme, setTheme] = useState<Theme>(() => {
		if (typeof window !== 'undefined') {
			const storedTheme = localStorage.getItem(storageKey) as Theme;
			return storedTheme || defaultTheme;
		}
		return defaultTheme;
	});

	useEffect(() => {
		const root = window.document.documentElement;

		// Remove previous theme classes
		root.classList.remove('light', 'dark');

		// Add current theme class
		root.classList.add(theme);

		// Store theme preference
		localStorage.setItem(storageKey, theme);
	}, [theme, storageKey]);

	const setThemeValue = (newTheme: Theme) => {
		setTheme(newTheme);
	};

	const toggleTheme = () => {
		setTheme((prevTheme) => (prevTheme === 'light' ? 'dark' : 'light'));
	};

	const value: ThemeContextType = {
		theme,
		setTheme: setThemeValue,
		toggleTheme,
	};

	return (
		<ThemeContext.Provider value={value}>
			<div
				className={`min-h-screen bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 transition-colors duration-200`}
			>
				{children}
			</div>
		</ThemeContext.Provider>
	);
};
